import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import api from '../lib/api'

interface Tenant {
  id: string
  name: string
  slug: string
  plan: string
  status: string
  max_profiles: number
  rate_limit_per_minute: number
}

interface User {
  id: string
  email: string
  name: string
  role: 'superuser' | 'admin' | 'employee' | 'tenant_admin' | 'consumer'
  tenant_id?: string
  tenant?: Tenant
  mfa_enabled?: boolean
  feature_overrides?: string[]
  canonical_id?: string
}

type TenantStatus = 'pending' | 'active' | 'suspended' | 'cancelled' | null

// Custom error for MFA required flow
export class MfaRequiredError extends Error {
  userId: string
  tenantStatus: TenantStatus

  constructor(userId: string, tenantStatus: TenantStatus) {
    super('MFA required')
    this.name = 'MfaRequiredError'
    this.userId = userId
    this.tenantStatus = tenantStatus
  }
}

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  tenantStatus: TenantStatus
  isAuthenticated: boolean
  isInitializing: boolean
  isLoading: boolean
  error: string | null

  // Actions
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  refresh: () => Promise<void>
  clearError: () => void

  // Status helpers
  isPending: () => boolean
  isSuspended: () => boolean
  isActive: () => boolean
  isCancelled: () => boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      tenantStatus: null,
      isAuthenticated: false,
      isInitializing: true,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })

        try {
          // OAuth2 form data
          const formData = new URLSearchParams()
          formData.append('username', email)
          formData.append('password', password)

          const response = await api.post('/auth/login', formData, {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          })

          const { access_token, refresh_token, tenant_status, mfa_required, user_id } = response.data

          // Check if MFA is required
          if (mfa_required && user_id) {
            set({ isLoading: false })
            throw new MfaRequiredError(user_id, tenant_status as TenantStatus)
          }

          // Set token in API client
          api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`

          // Get user profile
          const userResponse = await api.get('/auth/me')
          const user = userResponse.data

          set({
            user,
            accessToken: access_token,
            refreshToken: refresh_token,
            tenantStatus: tenant_status as TenantStatus,
            isAuthenticated: true,
            isLoading: false,
          })
        } catch (error: any) {
          // Re-throw MfaRequiredError without setting error state
          if (error instanceof MfaRequiredError) {
            throw error
          }

          const message = error.response?.data?.detail || error.response?.data?.error || 'Login failed'
          set({
            error: message,
            isLoading: false,
          })
          throw new Error(message)
        }
      },

      logout: () => {
        // Call backend to clear HTTP-only auth cookies
        api.post('/auth/logout').catch(() => {
          // Ignore errors — local cleanup still happens
        })

        // Clear API header
        delete api.defaults.headers.common['Authorization']

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          tenantStatus: null,
          isAuthenticated: false,
        })
      },

      refresh: async () => {
        const { refreshToken } = get()

        // Allow refresh via cookie even if no localStorage token
        try {
          const response = await api.post('/auth/refresh', refreshToken
            ? { refresh_token: refreshToken }
            : {}
          )

          const { access_token, refresh_token, tenant_status } = response.data

          // Update API header
          api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`

          set({
            accessToken: access_token,
            refreshToken: refresh_token,
            tenantStatus: tenant_status as TenantStatus,
          })
        } catch (error) {
          // Refresh failed, logout
          get().logout()
          throw error
        }
      },

      clearError: () => set({ error: null }),

      // Status helper methods
      isPending: () => get().tenantStatus === 'pending',
      isSuspended: () => get().tenantStatus === 'suspended',
      isActive: () => get().tenantStatus === 'active' || get().tenantStatus === null,
      isCancelled: () => get().tenantStatus === 'cancelled',
    }),
    {
      name: 'personic-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        tenantStatus: state.tenantStatus,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)

// Marketing domain: never bootstrap auth — clear any stale state and mark ready immediately
const _isMarketingHost = window.location.hostname === 'personicai.com' || window.location.hostname === 'www.personicai.com'

if (_isMarketingHost) {
  localStorage.removeItem('personic-auth')
  // Override any stale rehydrated state — marketing has zero protected routes
  useAuthStore.setState({
    user: null,
    accessToken: null,
    refreshToken: null,
    tenantStatus: null,
    isAuthenticated: false,
    isInitializing: false,
  })
} else {
  // Portal / other domains: initialize from localStorage or bootstrap from cookies
  const storedState = JSON.parse(localStorage.getItem('personic-auth') || '{}')
  if (storedState.state?.accessToken) {
    api.defaults.headers.common['Authorization'] = `Bearer ${storedState.state.accessToken}`
    useAuthStore.setState({ isInitializing: false })
  } else {
    // No localStorage session — try cookie-based refresh (cross-domain arrival)
    api.post('/auth/refresh', {})
      .then(async (response) => {
        const { access_token, refresh_token, tenant_status } = response.data
        api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`
        const userResp = await api.get('/auth/me')
        useAuthStore.setState({
          user: userResp.data,
          accessToken: access_token,
          refreshToken: refresh_token,
          tenantStatus: tenant_status as TenantStatus,
          isAuthenticated: true,
          isInitializing: false,
        })
      })
      .catch(() => {
        useAuthStore.setState({ isInitializing: false })
      })
  }
}
